import React, { useState } from 'react';
import { FiX } from 'react-icons/fi';

// A record saved under a retired option (e.g. the old bare 'Approved' job status) must still
// appear in its own dropdown, or the select renders blank and the next save rewrites the field.
const withCurrentOption = (options, current) => (
  current && !options.some(o => ((o && typeof o === 'object') ? o.value : o) === current)
    ? [...options, current]
    : options
);

// Generic form modal. Caller supplies fields + an async onSubmit(values).
// fields: [{ key, label, type: 'text'|'textarea'|'date'|'number'|'time'|'select'|'checkbox', options?, required?, hint? }]
export default function FormModal({ title, fields, initial = {}, onClose, onSubmit, submitLabel = 'Save' }) {
  const [form, setForm] = useState(() => {
    const f = {};
    fields.forEach(fd => {
      f[fd.key] = initial[fd.key] != null ? initial[fd.key] : (fd.type === 'checkbox' ? false : '');
    });
    return f;
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  // Per-field UI state for 'autocomplete' fields: { [key]: { query, open } }.
  const [acState, setAcState] = useState({});

  const setVal = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  // Change a field, and let that field optionally patch sibling fields (e.g. picking a
  // linked client autofills name/phone/email). fd.onChange(value, prevForm) => partial patch.
  const changeField = (fd, v) => setForm(prev => ({
    ...prev,
    [fd.key]: v,
    ...(fd.onChange ? (fd.onChange(v, prev) || {}) : {}),
  }));

  const save = async () => {
    // basic required validation
    const missing = fields.find(fd => fd.required && !String(form[fd.key] ?? '').trim());
    if (missing) { setError(`${missing.label} is required`); return; }
    setSaving(true);
    setError('');
    try {
      await onSubmit(form);
      onClose();
    } catch (e) {
      console.error('FormModal submit error:', e);
      setError(e?.response?.data?.error || 'Could not save');
      setSaving(false);
    }
  };

  return (
    <div className="qv-modal-overlay" onClick={onClose}>
      <div className="qv-modal" onClick={(e) => e.stopPropagation()}>
        <div className="qv-modal-head">
          <h3>{title}</h3>
          <button className="btn-ghost" onClick={onClose}><FiX /> Close</button>
        </div>
        <div className="qv-modal-body">
          {fields.map(fd => (
            <div key={fd.key} className={`qv-field${(fd.type === 'textarea' || fd.full) ? ' qv-field-wide' : ''}`}>
              {fd.type !== 'checkbox' && <label>{fd.label}{fd.required && ' *'}</label>}
              {fd.type === 'autocomplete' ? (() => {
                // Searchable single-select: type to filter instead of scrolling a long list.
                const st = acState[fd.key] || {};
                const opts = (fd.options || []).filter(o => (o && typeof o === 'object') ? o.value !== '' : o !== '');
                const selected = opts.find(o => String((o && typeof o === 'object') ? o.value : o) === String(form[fd.key] ?? ''));
                const selLabel = selected ? ((selected && typeof selected === 'object') ? selected.label : selected) : '';
                const display = st.query != null ? st.query : selLabel;
                const q = (st.query || '').trim().toLowerCase();
                const matches = (st.open && q)
                  ? opts.filter(o => String((o && typeof o === 'object') ? o.label : o).toLowerCase().includes(q)).slice(0, 50)
                  : [];
                const pick = (o) => {
                  const val = (o && typeof o === 'object') ? o.value : o;
                  setForm(prev => ({ ...prev, [fd.key]: val, ...(fd.onChange ? (fd.onChange(val, prev) || {}) : {}) }));
                  setAcState(s => ({ ...s, [fd.key]: { query: null, open: false } }));
                };
                return (
                  <div style={{ position: 'relative' }}>
                    <input
                      type="text"
                      value={display || ''}
                      placeholder={fd.placeholder || 'Type to search…'}
                      onChange={(e) => {
                        const v = e.target.value;
                        setAcState(s => ({ ...s, [fd.key]: { query: v, open: true } }));
                        if (v === '') setForm(prev => ({ ...prev, [fd.key]: '' }));  // clear = unlink
                      }}
                      onFocus={() => setAcState(s => ({ ...s, [fd.key]: { query: '', open: true } }))}
                      onBlur={() => setTimeout(() => setAcState(s => ({ ...s, [fd.key]: { query: null, open: false } })), 150)}
                    />
                    {matches.length > 0 && (
                      <div style={{
                        position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 20,
                        background: 'white', border: '1px solid #d1d5db', borderRadius: 6,
                        maxHeight: 220, overflowY: 'auto', boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                      }}>
                        {matches.map(o => {
                          const val = (o && typeof o === 'object') ? o.value : o;
                          const lbl = (o && typeof o === 'object') ? o.label : o;
                          return (
                            <div key={String(val)}
                              onMouseDown={(e) => { e.preventDefault(); pick(o); }}
                              style={{ padding: '8px 10px', cursor: 'pointer', fontSize: '0.9rem' }}
                              onMouseEnter={(e) => e.currentTarget.style.background = '#eff6ff'}
                              onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                            >{lbl}</div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })() : fd.type === 'select' ? (
                <select value={form[fd.key] ?? ''} onChange={(e) => changeField(fd, e.target.value)}>
                  {/* options may be plain strings, or { value, label } objects */}
                  {withCurrentOption(fd.options, form[fd.key]).map(o => {
                    const val = (o && typeof o === 'object') ? o.value : o;
                    const lbl = (o && typeof o === 'object') ? o.label : o;
                    return <option key={String(val)} value={val ?? ''}>{lbl || '— None —'}</option>;
                  })}
                </select>
              ) : fd.type === 'textarea' ? (
                <textarea rows={3} value={form[fd.key] || ''} onChange={(e) => changeField(fd, e.target.value)} />
              ) : fd.type === 'checkbox' ? (
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 500 }}>
                  <input type="checkbox" checked={!!form[fd.key]} onChange={(e) => setVal(fd.key, e.target.checked)} style={{ width: 'auto' }} />
                  {fd.label}
                </label>
              ) : (fd.type === 'date' || fd.type === 'time' || fd.type === 'datetime-local') ? (
                // Date/time fields get a Clear button — iOS/iPad's native picker has no way
                // to empty a set date, so this is the only reliable way to clear one there.
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input
                    type={fd.type}
                    value={form[fd.key] || ''}
                    onChange={(e) => changeField(fd, e.target.value)}
                    style={{ flex: 1 }}
                  />
                  {form[fd.key] ? (
                    <button type="button" onClick={() => changeField(fd, '')}
                      style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, background: 'white', color: '#374151', cursor: 'pointer', fontSize: 13, whiteSpace: 'nowrap', flexShrink: 0 }}>
                      Clear
                    </button>
                  ) : null}
                </div>
              ) : (
                <input
                  type={fd.type === 'number' ? 'number' : 'text'}
                  value={form[fd.key] || ''}
                  onChange={(e) => changeField(fd, e.target.value)}
                  placeholder={fd.placeholder || ''}
                />
              )}
              {fd.hint && <span style={{ fontSize: '0.72rem', color: '#9ca3af' }}>{fd.hint}</span>}
            </div>
          ))}
        </div>
        {error && <div style={{ color: '#dc2626', fontSize: '0.85rem', padding: '0 1.5rem' }}>{error}</div>}
        <div className="qv-modal-foot">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? 'Saving…' : submitLabel}</button>
        </div>
      </div>
    </div>
  );
}
